<script>
import { mapGetters } from 'vuex';
import CampaignContactGroupsAPI from 'dashboard/api/campaignContactGroups';
import CampaignsAPI from 'dashboard/api/campaigns';
import { format, formatDistanceToNow, addSeconds } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Spinner from 'shared/components/Spinner.vue';
import TableFooterPagination from 'dashboard/components/widgets/TableFooterPagination.vue';

export default {
  name: 'ViewCampaign',
  components: {
    Spinner,
    TableFooterPagination,
  },
  props: {
    campaign: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  data() {
    return {
      contactGroup: null,
      loadingGroup: false,
      recipients: [],
      recipientsMeta: {
        current_page: 1,
        total_pages: 1,
        total_count: 0,
      },
      loadingRecipients: false,
      currentPage: 1,
      perPage: 20,
      metricsRefreshInterval: null,
    };
  },
  computed: {
    ...mapGetters({
      allInboxes: 'inboxes/getInboxes',
    }),
    pageTitle() {
      return `${this.campaign.title}`;
    },
    hasMetrics() {
      return this.campaign.template_metadata?.metrics;
    },
    metrics() {
      return this.campaign.template_metadata?.metrics || {};
    },
    totalRecipients() {
      return this.metrics.total || 0;
    },
    plannedRecipients() {
      // Número de recipients planejados (antes da campanha iniciar)
      return this.campaign.template_metadata?.planner?.prepared || 0;
    },
    isScheduledNotStarted() {
      // Campanha agendada mas ainda não iniciada (sem recipients criados)
      return (this.campaign.workflow_state === 'scheduled' || this.campaign.workflow_state === 'draft') 
        && this.totalRecipients === 0 
        && this.plannedRecipients === 0;
    },
    audienceCount() {
      // Tenta obter contagem de diferentes fontes
      if (this.totalRecipients > 0) return this.totalRecipients;
      if (this.plannedRecipients > 0) return this.plannedRecipients;
      
      // Tentar obter do audience_source
      const source = this.campaign.audience_source || {};
      if (source.group_ids && source.group_ids.length > 0) {
        return this.contactGroup?.contacts_count || 0;
      }
      if (source.contact_ids && source.contact_ids.length > 0) {
        return source.contact_ids.length;
      }
      
      return 0;
    },
    completedCount() {
      const m = this.metrics;
      return (m.sent || 0) + (m.delivered || 0) + (m.read || 0) + (m.failed || 0) + (m.skipped || 0) + (m.cancelled || 0);
    },
    pendingCount() {
      const m = this.metrics;
      return (m.queued || 0) + (m.sending || 0);
    },
    completionPercentage() {
      if (this.totalRecipients === 0) return 0;
      return Math.round((this.completedCount / this.totalRecipients) * 100);
    },
    estimatedTimeRemaining() {
      if (this.pendingCount === 0 || !this.campaign.workflow_state) {
        return null;
      }
      
      // Se não está rodando, não há estimativa
      if (this.campaign.workflow_state !== 'running' && this.campaign.workflow_state !== 'scheduled') {
        return null;
      }
      
      // Tentar obter taxa de envio real dos recipients
      let secondsPerMessage = 60; // Valor padrão mais conservador
      
      // Calcular baseado nos recipients enviados (se houver dados históricos)
      const sentRecipients = this.recipients.filter(r => r.sent_at);
      if (sentRecipients.length >= 2) {
        // Calcular média de tempo entre envios
        const sortedBySent = [...sentRecipients].sort((a, b) => 
          new Date(a.sent_at) - new Date(b.sent_at)
        );
        
        let totalGap = 0;
        for (let i = 1; i < sortedBySent.length; i++) {
          const gap = (new Date(sortedBySent[i].sent_at) - new Date(sortedBySent[i-1].sent_at)) / 1000;
          totalGap += gap;
        }
        const avgGap = totalGap / (sortedBySent.length - 1);
        
        // Usar média calculada se for razoável (entre 5 e 120 segundos)
        if (avgGap >= 5 && avgGap <= 120) {
          secondsPerMessage = Math.round(avgGap);
        }
      }
      
      const totalSeconds = this.pendingCount * secondsPerMessage;
      
      return this.formatDuration(totalSeconds);
    },
    isRunning() {
      return this.campaign.workflow_state === 'running';
    },
    isPaused() {
      return this.campaign.workflow_state === 'paused';
    },
    isCompleted() {
      return this.campaign.workflow_state === 'completed';
    },
    isCancelled() {
      return this.campaign.workflow_state === 'cancelled';
    },
    inbox() {
      if (!this.campaign.inbox) return null;
      return this.allInboxes.find(inbox => inbox.id === this.campaign.inbox.id);
    },
    templateDetails() {
      if (!this.campaign.template && !this.campaign.template_name) return null;
      
      // Buscar template no inbox para obter o conteúdo
      let templateContent = null;
      if (this.inbox && this.inbox.message_templates) {
        const templateName = this.campaign.template?.name || this.campaign.template_name;
        const templateLanguage = this.campaign.template?.language || this.campaign.template_language;
        const template = this.inbox.message_templates.find(
          t =>
            (t.name === templateName || t.element_name === templateName) &&
            (t.language === templateLanguage || t.language_code === templateLanguage)
        );
        if (template) {
          // Extrair conteúdo do template
          if (template.components && Array.isArray(template.components)) {
            const body = template.components.find(
              component => component.type === 'BODY' && component.text
            );
            if (body) {
              templateContent = body.text;
            }
          }
          if (!templateContent && template.content) {
            templateContent = template.content;
          }
          if (!templateContent && template.data) {
            templateContent = template.data;
          }
        }
      }
      
      return {
        name: this.campaign.template?.name || this.campaign.template_name,
        language: this.campaign.template?.language || this.campaign.template_language,
        category: this.campaign.template?.category || this.campaign.template_category,
        provider: this.campaign.template_provider || this.campaign.template?.provider,
        content: templateContent,
      };
    },
    scheduledAtFormatted() {
      if (!this.campaign.scheduled_at) return null;
      const date = new Date(this.campaign.scheduled_at * 1000);
      return format(date, "dd/MM/yyyy 'às' HH:mm");
    },
    audienceInfo() {
      // audience pode ser um array de objetos {id, type} ou um objeto com group_ids/contact_ids
      if (!this.campaign.audience) {
        // Verificar se há recipients para determinar se a campanha foi enviada
        if (this.campaign.template_metadata?.metrics) {
          const total = Object.values(this.campaign.template_metadata.metrics).reduce((sum, val) => sum + (val || 0), 0);
          if (total > 0) {
            return {
              type: 'sent',
              count: total,
            };
          }
        }
        return null;
      }
      
      // Se audience é um array
      if (Array.isArray(this.campaign.audience)) {
        const groupItems = this.campaign.audience.filter(item => item.type === 'ContactGroup' || item.type === 'Group');
        const contactItems = this.campaign.audience.filter(item => item.type === 'Contact');
        
        if (groupItems.length > 0) {
          return {
            type: 'group',
            groupId: groupItems[0].id,
          };
        }
        
        if (contactItems.length > 0) {
          return {
            type: 'contacts',
            count: contactItems.length,
          };
        }
        
        // Se não encontrou, mas há itens, pode ser enviada
        if (this.campaign.audience.length > 0 && this.campaign.template_metadata?.metrics) {
          const total = Object.values(this.campaign.template_metadata.metrics).reduce((sum, val) => sum + (val || 0), 0);
          if (total > 0) {
            return {
              type: 'sent',
              count: total,
            };
          }
        }
      }
      
      // Se audience é um objeto
      if (this.campaign.audience.group_ids && this.campaign.audience.group_ids.length > 0) {
        return {
          type: 'group',
          groupId: this.campaign.audience.group_ids[0],
        };
      }
      
      if (this.campaign.audience.contact_ids && this.campaign.audience.contact_ids.length > 0) {
        return {
          type: 'contacts',
          count: this.campaign.audience.contact_ids.length,
        };
      }
      
      // Verificar se há recipients via metrics
      if (this.campaign.template_metadata?.metrics) {
        const total = Object.values(this.campaign.template_metadata.metrics).reduce((sum, val) => sum + (val || 0), 0);
        if (total > 0) {
          return {
            type: 'sent',
            count: total,
          };
        }
      }
      
      return null;
    },
  },
  mounted() {
    if (this.audienceInfo && this.audienceInfo.type === 'group') {
      this.loadGroup();
    }
    
    // Carregar destinatários se a campanha for do tipo template e tiver métricas
    if (this.campaign.template_campaign && this.hasMetrics) {
      this.loadRecipients();
      
      // Auto-refresh a cada 5 segundos se estiver rodando
      if (this.isRunning) {
        this.metricsRefreshInterval = setInterval(() => {
          this.refreshCampaignData();
        }, 5000);
      }
    }
  },
  beforeUnmount() {
    if (this.metricsRefreshInterval) {
      clearInterval(this.metricsRefreshInterval);
    }
  },
  methods: {
    async loadGroup() {
      if (!this.audienceInfo || this.audienceInfo.type !== 'group') return;
      
      this.loadingGroup = true;
      try {
        const { data } = await CampaignContactGroupsAPI.show(this.audienceInfo.groupId);
        this.contactGroup = data.group;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading group:', error);
      } finally {
        this.loadingGroup = false;
      }
    },
    async loadRecipients(page = 1, showLoading = true) {
      if (showLoading) {
        this.loadingRecipients = true;
      }
      
      try {
        const response = await CampaignsAPI.recipients(this.campaign.id, {
          page,
          per_page: this.perPage,
        });
        
        this.recipients = response.data.recipients || [];
        this.recipientsMeta = response.data.meta || {
          current_page: 1,
          total_pages: 1,
          total_count: 0,
        };
        this.currentPage = page;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading recipients:', error);
        
        // Se 404, a campanha não existe mais
        if (error.response && error.response.status === 404) {
          this.recipients = [];
          this.recipientsMeta = {
            current_page: 1,
            total_pages: 1,
            total_count: 0,
          };
        }
      } finally {
        if (showLoading) {
          this.loadingRecipients = false;
        }
      }
    },
    async refreshCampaignData() {
      try {
        // Buscar campanha atualizada da API
        const response = await CampaignsAPI.show(this.campaign.id);
        const updatedCampaign = response.data;
        
        // Atualizar APENAS as propriedades que mudam (evita piscar)
        // Não atualizar o objeto inteiro para evitar re-renderização completa
        if (this.campaign.template_metadata !== updatedCampaign.template_metadata) {
          this.campaign.template_metadata = updatedCampaign.template_metadata;
        }
        if (this.campaign.workflow_state !== updatedCampaign.workflow_state) {
          this.campaign.workflow_state = updatedCampaign.workflow_state;
        }
        
        // Atualizar recipients da página atual (sem mostrar loading)
        await this.loadRecipients(this.currentPage, false);
        
        // Se a campanha foi concluída, parar o auto-refresh
        if (this.campaign.workflow_state === 'completed' || this.campaign.workflow_state === 'cancelled') {
          if (this.metricsRefreshInterval) {
            clearInterval(this.metricsRefreshInterval);
            this.metricsRefreshInterval = null;
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error refreshing campaign data:', error);
        
        // Se a campanha não existe mais (404), parar o auto-refresh e fechar o modal
        if (error.response && error.response.status === 404) {
          // eslint-disable-next-line no-console
          console.warn('Campaign not found, stopping auto-refresh');
          
          if (this.metricsRefreshInterval) {
            clearInterval(this.metricsRefreshInterval);
            this.metricsRefreshInterval = null;
          }
          
          // Fechar o modal após um pequeno delay
          setTimeout(() => {
            this.onClose();
          }, 1000);
        }
      }
    },
    onPageChange(page) {
      this.loadRecipients(page);
    },
    formatDuration(seconds) {
      if (seconds < 60) {
        return `${seconds}s`;
      }
      
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      
      if (minutes < 60) {
        // Mostrar minutos e segundos (ex: "2min 59s")
        return remainingSeconds > 0
          ? `${minutes}min ${remainingSeconds}s`
          : `${minutes}min`;
      }
      
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      
      if (hours < 24) {
        // Mostrar horas e minutos (ex: "2h 30min")
        return remainingMinutes > 0
          ? `${hours}h ${remainingMinutes}min`
          : `${hours}h`;
      }
      
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      
      // Mostrar dias e horas (ex: "2d 5h")
      return remainingHours > 0
        ? `${days}d ${remainingHours}h`
        : `${days}d`;
    },
    formatRecipientTime(timestamp) {
      if (!timestamp) return '—';
      
      try {
        const date = new Date(timestamp);
        return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
      } catch {
        return '—';
      }
    },
    getStateColor(state) {
      const colors = {
        pending: 'secondary',
        scheduled: 'secondary',
        queued: 'warning',
        sending: 'warning',
        sent: 'success',
        delivered: 'success',
        read: 'success',
        failed: 'alert',
        skipped: 'secondary',
        cancelled: 'secondary',
      };
      return colors[state] || 'secondary';
    },
    getStateLabel(state) {
      const labels = {
        pending: 'Pendente',
        scheduled: 'Agendado',
        queued: 'Na fila',
        sending: 'Enviando',
        sent: 'Enviado',
        delivered: 'Entregue',
        read: 'Lida',
        failed: 'Falhou',
        skipped: 'Ignorado',
        cancelled: 'Cancelado',
      };
      return labels[state] || state;
    },
    translatedCategory(category) {
      if (!category) return category;
      const map = {
        UTILITY: 'Utilitário',
        MARKETING: 'Marketing',
        AUTHENTICATION: 'Autenticação',
      };
      const upperCategory = category.toUpperCase();
      return map[upperCategory] || category;
    },
    translatedTemplateType(type) {
      if (!type) return type;
      const map = {
        TEXT: 'Texto',
        MEDIA: 'Mídia',
        INTERACTIVE: 'Interativo',
      };
      return map[type.toUpperCase()] || type;
    },
    translatedLanguage(code) {
      if (!code) return code;
      const map = {
        pt_BR: 'Português (Brasil)',
        en_US: 'Inglês (EUA)',
        es_ES: 'Espanhol (Espanha)',
        pt: 'Português',
        en: 'Inglês',
        es: 'Espanhol',
      };
      return map[code] || code;
    },
    translatedStatus(status) {
      if (!status) return '';
      const map = {
        APPROVED: 'Aprovado',
        PENDING: 'Pendente',
        REJECTED: 'Rejeitado',
        IN_REVIEW: 'Em análise',
        PAUSED: 'Pausado',
        DISABLED: 'Desativado',
        SENT_FOR_REVIEW: 'Enviado para revisão',
      };
      return map[status.toUpperCase()] || status;
    },
    translatedQuality(quality) {
      if (!quality) return '';
      const map = {
        GREEN: 'Alta',
        YELLOW: 'Média',
        RED: 'Baixa',
        UNKNOWN: 'Desconhecida',
      };
      return map[quality.toUpperCase()] || quality;
    },
    getStatusTranslation(status) {
      if (!status) return '';
      
      // Mapear workflow_state para status traduzidos
      const workflowStatusMap = {
        draft: 'Rascunho',
        scheduled: 'Agendada',
        running: 'Em execução',
        paused: 'Pausada',
        completed: 'Concluída',
        cancelled: 'Cancelada',
      };
      
      const lowerStatus = status.toLowerCase();
      if (workflowStatusMap[lowerStatus]) {
        return workflowStatusMap[lowerStatus];
      }
      
      // Tentar buscar tradução i18n
      const statusKey = 'CAMPAIGN.TEMPLATE.STATUS.' + status.toUpperCase();
      const translation = this.$t(statusKey);
      if (translation !== statusKey) {
        return translation;
      }
      
      // Fallback para tradução genérica
      return this.translatedStatus(status);
    },
    onClose() {
      this.$emit('close');
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto p-6">
    <div class="text-center mb-6">
      <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
        {{ pageTitle }}
      </h2>
    </div>
    
    <div class="space-y-6 mt-4">
      <!-- Título -->
      <div class="grid gap-2">
        <label class="woot-form-label text-sm font-semibold">
          {{ $t('CAMPAIGN.ADD.FORM.TITLE.LABEL') }}
        </label>
        <div class="text-base text-slate-700 dark:text-slate-200">
          {{ campaign.title || '—' }}
        </div>
      </div>

      <!-- Inbox -->
      <div class="grid gap-2">
        <label class="woot-form-label text-sm font-semibold">
          {{ $t('CAMPAIGN.VIEW.INBOX_LABEL') || 'Canal' }}
        </label>
        <div class="text-base text-slate-700 dark:text-slate-200">
          {{ inbox ? inbox.name : (campaign.inbox ? campaign.inbox.name : '—') }}
        </div>
      </div>

      <!-- Template -->
      <div v-if="templateDetails" class="grid gap-2">
        <label class="woot-form-label text-sm font-semibold">
          {{ $t('CAMPAIGN.ADD.TEMPLATE.TEMPLATE_LABEL') }}
        </label>
        <div class="space-y-2">
          <div class="text-base font-medium text-slate-700 dark:text-slate-200">
            {{ templateDetails.name }}
          </div>
          <div class="text-sm text-slate-600 dark:text-slate-400">
            {{ translatedLanguage(templateDetails.language) }} · {{ translatedCategory(templateDetails.category) }}
          </div>
          <div v-if="templateDetails.content" class="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">
            {{ templateDetails.content }}
          </div>
        </div>
      </div>

      <!-- Variable Mapping -->
      <div v-if="campaign.variable_mapping && Object.keys(campaign.variable_mapping).length > 0" class="grid gap-2">
        <label class="woot-form-label text-sm font-semibold">
          {{ $t('CAMPAIGN.ADD.TEMPLATE.VARIABLE_MAPPING.LABEL') }}
        </label>
        <div class="space-y-2">
          <div
            v-for="(config, placeholder) in campaign.variable_mapping"
            :key="placeholder"
            class="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
          >
            <div class="text-sm font-medium text-slate-700 dark:text-slate-200">
              {{ placeholder }}
            </div>
            <div class="text-xs text-slate-600 dark:text-slate-400 mt-1">
              <template v-if="config.source === 'static'">
                {{ config.value || '—' }}
              </template>
              <template v-else-if="config.source === 'contact' && config.attribute">
                {{ $t('CAMPAIGN.ADD.TEMPLATE.MAPPING_OPTIONS.CONTACT_' + config.attribute.toUpperCase()) }}
              </template>
              <template v-else>
                {{ JSON.stringify(config) }}
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Audience -->
      <div class="grid gap-2">
        <label class="woot-form-label text-sm font-semibold">
          {{ $t('CAMPAIGN.ADD.CONTACTS.AUDIENCE_LABEL') }}
        </label>
        <div v-if="loadingGroup" class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <Spinner size="16" />
          <span>{{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.LOADING') }}</span>
        </div>
        <div v-else-if="audienceInfo && audienceInfo.type === 'group' && contactGroup" class="text-base text-slate-700 dark:text-slate-200">
          {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP_SELECTOR.SUMMARY_LABEL', {
            name: contactGroup.name,
            count: contactGroup.contacts_count || 0,
          }) }}
        </div>
        <div v-else-if="audienceInfo && audienceInfo.type === 'contacts'" class="text-base text-slate-700 dark:text-slate-200">
          {{ $t('CAMPAIGN.ADD.CONTACTS.SELECTED_COUNT', { count: audienceInfo.count }) }}
        </div>
        <div v-else-if="audienceInfo && audienceInfo.type === 'sent'" class="text-base text-slate-700 dark:text-slate-200">
          {{ $t('CAMPAIGN.VIEW.SENT_TO_COUNT', { count: audienceInfo.count }) }}
        </div>
        <div v-else class="text-base text-slate-500 dark:text-slate-400">
          {{ $t('CAMPAIGN.ADD.CONTACTS.NO_SELECTION') }}
        </div>
      </div>

      <!-- Scheduled At -->
      <div v-if="scheduledAtFormatted" class="grid gap-2">
        <label class="woot-form-label text-sm font-semibold">
          {{ $t('CAMPAIGN.ADD.TEMPLATE.SCHEDULED_AT_LABEL') }}
        </label>
        <div class="text-base text-slate-700 dark:text-slate-200">
          {{ scheduledAtFormatted }}
        </div>
      </div>

      <!-- Status -->
      <div class="grid gap-2">
        <label class="woot-form-label text-sm font-semibold">
          {{ $t('CAMPAIGN.LIST.STATUS_LABEL') }}
        </label>
        <div class="text-base">
          <woot-label
            v-if="campaign.workflow_state"
            :title="getStatusTranslation(campaign.workflow_state)"
            :color-scheme="campaign.workflow_state === 'running' ? 'success' : campaign.workflow_state === 'paused' ? 'warning' : 'secondary'"
          />
        </div>
      </div>

      <!-- Informação de Campanha Agendada (ainda não iniciada) -->
      <div v-if="isScheduledNotStarted && audienceCount > 0" class="mt-8">
        <div class="border-t border-slate-200 dark:border-slate-700 pt-6">
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
            {{ $t('CAMPAIGN.VIEW.PROGRESS.TITLE') }}
          </h3>
          
          <div class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div class="flex items-start gap-3">
              <div class="flex-shrink-0">
                <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="flex-1">
                <h4 class="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                  {{ $t('CAMPAIGN.VIEW.PROGRESS.SCHEDULED_NOT_STARTED_TITLE') }}
                </h4>
                <p class="text-sm text-blue-700 dark:text-blue-300">
                  {{ $t('CAMPAIGN.VIEW.PROGRESS.SCHEDULED_NOT_STARTED_MESSAGE', {
                    count: audienceCount,
                    date: scheduledAtFormatted
                  }) }}
                </p>
                <div class="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                  <div class="text-xs text-blue-600 dark:text-blue-400">
                    <strong>{{ audienceCount }}</strong> {{ $t('CAMPAIGN.VIEW.PROGRESS.RECIPIENTS') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Progresso da Campanha (em execução ou concluída) -->
      <div v-if="hasMetrics && totalRecipients > 0" class="mt-8">
        <div class="border-t border-slate-200 dark:border-slate-700 pt-6">
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
            {{ $t('CAMPAIGN.VIEW.PROGRESS.TITLE') }}
          </h3>
          
          <!-- Resumo de progresso -->
          <div class="mb-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
            <div class="flex items-center justify-between">
              <div>
                <div class="text-4xl font-bold text-blue-600 dark:text-blue-400">
                  {{ completionPercentage }}%
                </div>
                <div class="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  {{ completedCount }} de {{ totalRecipients }} {{ $t('CAMPAIGN.VIEW.PROGRESS.RECIPIENTS') }}
                </div>
              </div>
              <div v-if="estimatedTimeRemaining && isRunning" class="text-right">
                <div class="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                  {{ $t('CAMPAIGN.VIEW.PROGRESS.ESTIMATED_TIME') }}
                </div>
                <div class="text-2xl font-semibold text-slate-700 dark:text-slate-200 mt-1">
                  {{ estimatedTimeRemaining }}
                </div>
              </div>
            </div>
          </div>

          <!-- Métricas detalhadas -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <!-- Na fila / Pendentes -->
            <div v-if="metrics.queued > 0" class="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <div class="text-xs text-yellow-700 dark:text-yellow-300 font-medium mb-1">
                {{ $t('CAMPAIGN.ADD.TEMPLATE.METRICS.QUEUED') }}
              </div>
              <div class="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                {{ metrics.queued }}
              </div>
            </div>

            <!-- Enviando -->
            <div v-if="metrics.sending > 0" class="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div class="text-xs text-blue-700 dark:text-blue-300 font-medium mb-1">
                {{ $t('CAMPAIGN.ADD.TEMPLATE.METRICS.SENDING') }}
              </div>
              <div class="text-2xl font-bold text-blue-900 dark:text-blue-100">
                {{ metrics.sending }}
              </div>
            </div>

            <!-- Enviados -->
            <div v-if="metrics.sent > 0" class="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div class="text-xs text-green-700 dark:text-green-300 font-medium mb-1">
                {{ $t('CAMPAIGN.ADD.TEMPLATE.METRICS.SENT') }}
              </div>
              <div class="text-2xl font-bold text-green-900 dark:text-green-100">
                {{ metrics.sent }}
              </div>
            </div>

            <!-- Entregues -->
            <div v-if="metrics.delivered > 0" class="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div class="text-xs text-green-700 dark:text-green-300 font-medium mb-1">
                {{ $t('CAMPAIGN.ADD.TEMPLATE.METRICS.DELIVERED') }}
              </div>
              <div class="text-2xl font-bold text-green-900 dark:text-green-100">
                {{ metrics.delivered }}
              </div>
            </div>

            <!-- Lidas -->
            <div v-if="metrics.read > 0" class="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div class="text-xs text-green-700 dark:text-green-300 font-medium mb-1">
                {{ $t('CAMPAIGN.ADD.TEMPLATE.METRICS.READ') }}
              </div>
              <div class="text-2xl font-bold text-green-900 dark:text-green-100">
                {{ metrics.read }}
              </div>
            </div>

            <!-- Falhas -->
            <div v-if="metrics.failed > 0" class="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <div class="text-xs text-red-700 dark:text-red-300 font-medium mb-1">
                {{ $t('CAMPAIGN.ADD.TEMPLATE.METRICS.FAILED') }}
              </div>
              <div class="text-2xl font-bold text-red-900 dark:text-red-100">
                {{ metrics.failed }}
              </div>
            </div>

            <!-- Ignorados -->
            <div v-if="metrics.skipped > 0" class="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <div class="text-xs text-slate-700 dark:text-slate-300 font-medium mb-1">
                {{ $t('CAMPAIGN.ADD.TEMPLATE.METRICS.SKIPPED') }}
              </div>
              <div class="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {{ metrics.skipped }}
              </div>
            </div>

            <!-- Cancelados -->
            <div v-if="metrics.cancelled > 0" class="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <div class="text-xs text-slate-700 dark:text-slate-300 font-medium mb-1">
                {{ $t('CAMPAIGN.VIEW.PROGRESS.CANCELLED') }}
              </div>
              <div class="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {{ metrics.cancelled }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Lista de Destinatários -->
      <div v-if="hasMetrics && totalRecipients > 0" class="mt-8">
        <div class="border-t border-slate-200 dark:border-slate-700 pt-6">
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
            {{ $t('CAMPAIGN.ADD.TEMPLATE.RECIPIENTS.TITLE') }}
            <span class="text-sm font-normal text-slate-600 dark:text-slate-400">
              ({{ recipientsMeta.total_count }})
            </span>
          </h3>

          <!-- Loading -->
          <div v-if="loadingRecipients" class="flex items-center justify-center py-8">
            <Spinner size="32" />
            <span class="ml-3 text-slate-600 dark:text-slate-400">
              {{ $t('CAMPAIGN.ADD.TEMPLATE.RECIPIENTS.LOADING') }}
            </span>
          </div>

          <!-- Tabela de destinatários -->
          <div v-else-if="recipients.length > 0" class="overflow-x-auto">
            <table class="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead class="bg-slate-50 dark:bg-slate-800">
                <tr>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                    {{ $t('CAMPAIGN.ADD.TEMPLATE.RECIPIENTS.PHONE') }}
                  </th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                    {{ $t('CAMPAIGN.VIEW.PROGRESS.CONTACT_NAME') }}
                  </th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                    {{ $t('CAMPAIGN.ADD.TEMPLATE.RECIPIENTS.STATE') }}
                  </th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                    {{ $t('CAMPAIGN.ADD.TEMPLATE.RECIPIENTS.ATTEMPTS') }}
                  </th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                    {{ $t('CAMPAIGN.ADD.TEMPLATE.RECIPIENTS.UPDATED') }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-700">
                <tr v-for="recipient in recipients" :key="recipient.id" class="hover:bg-slate-50 dark:hover:bg-slate-800">
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100">
                    {{ recipient.phone }}
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-slate-700 dark:text-slate-300">
                    {{ recipient.contact && recipient.contact.name || '—' }}
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    <woot-label
                      :title="getStateLabel(recipient.state)"
                      :color-scheme="getStateColor(recipient.state)"
                      small
                    />
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-slate-700 dark:text-slate-300">
                    {{ recipient.attempts }}
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400">
                    {{ formatRecipientTime(recipient.updated_at) }}
                  </td>
                </tr>
                <tr v-if="recipients.some(r => r.error_message)" class="bg-red-50 dark:bg-red-900/10">
                  <td colspan="5" class="px-4 py-2">
                    <details class="text-xs">
                      <summary class="cursor-pointer text-red-700 dark:text-red-300 font-medium">
                        {{ $t('CAMPAIGN.VIEW.PROGRESS.VIEW_ERRORS') }}
                      </summary>
                      <div class="mt-2 space-y-2">
                        <div
                          v-for="recipient in recipients.filter(r => r.error_message)"
                          :key="recipient.id"
                          class="p-2 bg-white dark:bg-slate-900 rounded border border-red-200 dark:border-red-800"
                        >
                          <div class="font-medium text-slate-900 dark:text-slate-100">
                            {{ recipient.phone }}
                          </div>
                          <div class="text-red-600 dark:text-red-400">
                            {{ recipient.error_message }}
                          </div>
                        </div>
                      </div>
                    </details>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Paginação -->
            <div v-if="recipientsMeta.total_pages > 1" class="mt-4">
              <TableFooterPagination
                :current-page="recipientsMeta.current_page"
                :total-count="recipientsMeta.total_count"
                :page-size="perPage"
                @page-change="onPageChange"
              />
            </div>
          </div>

          <!-- Empty state -->
          <div v-else class="text-center py-8 text-slate-600 dark:text-slate-400">
            {{ $t('CAMPAIGN.ADD.TEMPLATE.RECIPIENTS.EMPTY') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Botão Fechar -->
    <div class="flex justify-end gap-2 pt-6 mt-6 border-t border-slate-200 dark:border-slate-700">
      <woot-button variant="clear" type="button" @click="onClose">
        {{ $t('CAMPAIGN.VIEW.CLOSE') }}
      </woot-button>
    </div>
  </div>
</template>
